// @flow
/** @jsx jsx */

import {
  compact,
  filter,
  indexOf,
  map,
  pipe,
  prop,
  sortBy,
  toPairs,
} from 'lodash/fp';

import { jsx } from '@emotion/core';
import { MessageBar, ScrollIntoViewIfNeeded } from '@zapier/common-components';

import type { FieldError } from '@zapier/payment/src/types';

const FIELDS_ORDER = [
  'number',
  'name',
  'expiry',
  'cvc',
  'addressCountry',
  'addressState',
  'addressZip',
];

const toErrorsArray = pipe([
  toPairs,
  sortBy(([fieldName]) => indexOf(fieldName, FIELDS_ORDER)),
  map('1'),
  filter(Boolean),
]);

const countFixedErrors = (errors): number =>
  pipe([map('isFixed'), compact, prop('length')])(errors);

const getMessageBarKind = (errors, fixedErrorsCount) =>
  fixedErrorsCount === errors.length ? 'success' : 'error';

type Props = {
  /** Array of Field Errors to show */
  errors: FieldError[],
};

const textCss = (isFixed: boolean = false) => ({
  fontWeight: isFixed ? 400 : 600,
  textDecoration: isFixed ? 'line-through' : 'none',
});

const renderError = error => (
  <li css={textCss(error.isFixed)} key={error.message}>
    {error.message}
  </li>
);

const CreditCardErrors = (props: Props) => {
  const errors = toErrorsArray(props.errors);
  const fixedErrorsCount = countFixedErrors(errors);
  const title =
    fixedErrorsCount < errors.length
      ? "We're almost there! The following items need your attention:"
      : 'Everything looks good now!';

  return (
    <ScrollIntoViewIfNeeded>
      <MessageBar
        margin={{ vertical: 20 }}
        title={title}
        type={getMessageBarKind(errors, fixedErrorsCount)}
      >
        {Array.isArray(errors) && errors.length > 0 && (
          <MessageBar.MessageList>
            {map(renderError, errors)}
          </MessageBar.MessageList>
        )}
      </MessageBar>
    </ScrollIntoViewIfNeeded>
  );
};

export default CreditCardErrors;
